import React from "react";
import {
  Text,
  Skeleton,
  HStack,
  Avatar,
  Spacer,
  Button,
  VStack,
  Tag,
} from "@chakra-ui/react";
import { CalendarIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import Link from "next/link";

import SportIconsFactory from "components/Icons/SportIconsFactory";
import { formatDate } from "shared/utils/dateFormater";
import { getPaths } from "lib/utils/paths";
import { useTranslations } from "next-intl";
import type { IAthletesUploadsQuery } from "lib/api/sdk";
import { flattenDeep } from "lodash";

interface IItem {
  athlete?: IAthletesUploadsQuery["athletes"][0];
  isLoading?: boolean;
}


const Item = ({ athlete, isLoading }: IItem) => {
  const t = useTranslations("pages.homePage.newUploads");
  const workouts = flattenDeep(athlete?.athlete_blocks?.map(({ athlete_weeks }) => athlete_weeks?.map(({ athlete_workouts }) => athlete_workouts))) || [];

  // Highlight the last submitted workout, in case there are 2 or more
  const lastWorkoutId = workouts[workouts.length - 1]?.id;

  return (
    <HStack w="full" spacing={5} justify={"space-between"} align={"baseline"}>
      <HStack flex={1} align={"flex-end"}>
        <VStack align={"flex-start"}>
          <HStack flex={1} spacing={3} align={"flex-start"}>
            <Avatar size="md" src={athlete?.avatar} />
            <VStack align={"flex-start"} spacing={1}>
              <Skeleton isLoaded={!isLoading} minH={15}>
                <Text
                  fontSize={"lg"}
                  fontWeight={"bold"}
                  overflow="hidden"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                >
                  {athlete?.name}
                </Text>
              </Skeleton>
              <VStack align={"stretch"}>
                {workouts?.slice(0, 2).map(workout => (
                  <Skeleton isLoaded={!isLoading} minH={15} key={workout.id}>
                    <HStack spacing={3}>
                      <HStack spacing={2}>
                        <CalendarIcon fontSize={"sm"} />
                        <Text fontSize={"sm"}>
                          {formatDate(workout.date, "d MMM")}
                        </Text>
                      </HStack>
                      <Tag size="sm">
                        <HStack spacing={1}>
                          <Text fontSize={"sm"} fontWeight={"bold"}>
                            {
                              workout.athlete_workout_sections.find(
                                section => section.type === "main" || section.type === "series_main"
                              )?.workout_zone?.name
                            }
                          </Text>
                          <SportIconsFactory id={workout.workout_type.avatar} />
                        </HStack>
                      </Tag>
                      <HStack spacing={1}>
                        <Text fontSize={"sm"} fontStyle={"italic"}>
                          {workout.comments}
                        </Text>
                      </HStack>
                    </HStack>
                  </Skeleton>
                ))}
                {(workouts?.length > 2 && !isLoading) && (
                  <Text fontSize={"sm"} color="gray.300">
                    {t("xMoreUploads", {
                      count: workouts?.length - 2,
                    })}
                  </Text>
                )}
              </VStack>
            </VStack>
          </HStack>
        </VStack>
        <Spacer />
        <Link
          passHref
          // eslint-disable-next-line i18next/no-literal-string
          href={`${getPaths.atletas.id(athlete?.id)}?tab=workouts${lastWorkoutId ? `&h=${lastWorkoutId}` : ""}`}
        >
          <Button
            rightIcon={<ExternalLinkIcon />}
            size="sm"
            colorScheme="brandBlue"
            isDisabled={isLoading}
          >
            {t("viewUploads")}
          </Button>
        </Link>
      </HStack>
    </HStack>
  );
};

export default Item;
